import React, { useEffect, useRef } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import logo from "../../assets/Images/Logo.png";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getCustomInvoiceDetails } from "../../stores/clients";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ListLoadingNew from "../common-components/ListLoadingNew";

const ViewCustomInvoice = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { invoice_id } = useParams();
  const [invoiceDetails, setInvoiceDetails] = React.useState<any>(null);
  const [lineItemsTotal, setLineItemsTotal] = React.useState<number>(0);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);

  const dispatch = useDispatch();

  const getInvoiceDetails = async () => {
    try {
      // Replace with actual action to fetch invoice details
      const res = await dispatch<any>(
        getCustomInvoiceDetails(invoice_id ? invoice_id : ""),
      ).unwrap();
      if (res?.code === 404) {
        navigate("/tools/invoices");
      }
      setInvoiceDetails(res?.data);
      // Calculate total for line items and store in state
      const items = res?.data?.line_items || [];
      const total = items.reduce(
        (sum: number, item: any) =>
          sum + (item.unit_cost ?? 0) * (item.quantity ?? 1),
        0,
      );
      setLineItemsTotal(total);
    } catch (error) {
      console.error("Failed to fetch invoice details:", error);
    }
  };

  useEffect(() => {
    getInvoiceDetails();
  }, [invoice_id]);

  useEffect(() => {
    const Timeout = setTimeout(() => {
      setLoading(false);
    }, 4000);
    return () => clearTimeout(Timeout);
  }, []);

  const handleDownload = async () => {
    if (!invoiceRef.current) return;

    // Hide buttons
    const downloadBtn = invoiceRef.current.querySelector(
      "#download-invoice-btn",
    ) as HTMLElement;
    const backBtn = invoiceRef.current.querySelector(
      "#back-invoice-btn",
    ) as HTMLElement;
    if (downloadBtn) downloadBtn.style.display = "none";
    if (backBtn) backBtn.style.display = "none";

    // Capture invoice as canvas
    const canvas = await html2canvas(invoiceRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
    });
    const imgData = canvas.toDataURL("image/png");

    // Standard A4 size in px at 72 DPI
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Calculate scaling
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

    const newWidth = imgWidth * ratio;
    const newHeight = imgHeight * ratio;

    // Center it on the page
    const x = (pdfWidth - newWidth) / 2;
    const y = 20; // some top margin

    pdf.addImage(imgData, "PNG", x, y, newWidth, newHeight);

    pdf.save("invoice.pdf");

    // Restore buttons
    if (downloadBtn) downloadBtn.style.display = "";
    if (backBtn) backBtn.style.display = "";
  };

  if (loading) {
    return <ListLoadingNew />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#F6F9FD",
        minHeight: "100vh",
        width: "100%",
        p: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          bgcolor: "#2977CC",
          boxShadow: 2,
          display: "flex",
          justifyContent: "center", // centers content
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 5 },
          py: 1,
          zIndex: 1000,
        }}
      >
        <img
          src={logo}
          alt="Company Logo"
          style={{
            width: "200px",
            height: "75px",
            objectFit: "contain", // keeps proportions
          }}
        />
      </Box>

      {invoiceDetails?.invoice?.generated_invoice_id && (
        <Box
          ref={invoiceRef}
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 5,
            width: "100%",
            maxWidth: "900px",
            height: isMobile ? "auto" : "screen",
            marginTop: 15,
            marginBottom: 5,
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <div
            id="back-invoice-btn"
            onClick={() => navigate(-1)}
            style={{
              cursor: "pointer",
              color: "#2977CC",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ArrowBackIosNewIcon /> back
          </div>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={700}
              textAlign="left"
              fontSize="30px"
              mb={2}
            >
              Invoice
            </Typography>
            <button
              id="download-invoice-btn"
              style={{
                fontSize: "14px",
                textDecoration: "underline",
                cursor: "pointer",
                color: "#2977CC",
                background: "none",
                border: "none",
                paddingBottom: 18,
              }}
              onClick={handleDownload}
            >
              Download invoice
            </button>
          </div>

          {/* Invoice Info */}
          <Box sx={{ mb: 4 }}>
            <Typography
              fontSize={{ xs: 14, sm: 15 }}
              fontWeight={600}
              textAlign="left"
            >
              Invoice number
              <span style={{ marginLeft: 10 }}>
                {invoiceDetails?.invoice?.generated_invoice_id}
              </span>
            </Typography>
            <Typography

              fontSize={{ xs: 14, sm: 15 }}
              fontWeight={600}
              textAlign="left"
            >
              Date of issue
              <span style={{ marginLeft: 28 }}>
                {dayjs(invoiceDetails?.invoice?.invoice_date).format(
                  "D MMMM YYYY",
                )}
              </span>
            </Typography>
            <Typography
              fontSize={{ xs: 14, sm: 15 }}
              fontWeight={600}
              textAlign="left"
            >
              Date due
              <span style={{ marginLeft: 56 }}>
                {dayjs(invoiceDetails?.invoice?.due_date).format("D MMMM YYYY")}
              </span>
            </Typography>
          </Box>

          {/* Billing Info */}
          <Box sx={{ mb: 3 }}>
            <Typography fontWeight={600}>
              {invoiceDetails?.invoice?.customer_name}
            </Typography>
            <Typography fontSize="14px">
              {invoiceDetails?.invoice?.customer_email}
            </Typography>
            <Typography fontSize="14px">
              {invoiceDetails?.invoice?.customer_address}
            </Typography>
            <Typography fontSize="14px">
              {invoiceDetails?.invoice?.customer_country}
            </Typography>
          </Box>

          {/* Notes */}
          <Typography mb={2} fontSize={14}>
            {invoiceDetails?.invoice?.notes}
          </Typography>

          {/* Items Table */}
          <Box sx={{ width: "100%", overflowX: "auto", mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="center">Unit price</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceDetails?.line_items?.length > 0 ? (
                  invoiceDetails?.line_items.map((item: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{item.item_name}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="center">
                        ${item.unit_cost?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        $
                        {((item.unit_cost ?? 0) * (item.quantity ?? 1)).toFixed(
                          2,
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No items found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <div
            style={{
              display: isMobile ? "grid" : "flex",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : 400, fontSize: 14 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 600,
                  fontSize: 16,
                  py: 0.5,
                }}
              >
                Bank Payment Details
              </Box>
              {invoiceDetails?.invoice?.bank_details ? (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#fff",
                    color: "#333",
                    whiteSpace: "pre-line",
                  }}
                >
                  {invoiceDetails?.invoice?.bank_details}
                </div>
              ) : (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor: "#fff",
                    color: "#333",
                    whiteSpace: "pre-line",
                  }}
                >
                  No data found
                </div>
              )}
            </Box>

            {/* Totals */}
            <Box
              sx={{
                width: 320,
                fontSize: 14,
                mt: isMobile ? 3 : 0,
                ml: isMobile ? 0 : "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 600,
                  fontSize: 16,
                  py: 0.5,
                }}
              >
                Invoice Summary
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #ccc",
                  py: 0.5,
                }}
              >
                <Typography fontSize={14}>Subtotal</Typography>
                <Typography fontSize={14}>
                  ${lineItemsTotal.toFixed(2)}
                </Typography>
              </Box>
              {/* <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #ccc",
                  py: 0.5,
                }}
              >
                <Typography fontSize={14}>
                  Tax ({invoiceDetails?.invoice?.tax_rate}% on $
                  {lineItemsTotal.toFixed(2)})
                </Typography>
                <Typography fontSize={14}>
                  $
                  {(
                    invoiceDetails?.invoice?.total_amount - lineItemsTotal
                  ).toFixed(2)}
                </Typography>
              </Box> */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 1,
                }}
              >
                <Typography fontWeight={600} fontSize={14}>
                  Total
                </Typography>
                <Typography fontWeight={600} fontSize={14}>
                  ${invoiceDetails?.invoice?.total_amount?.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </div>
        </Box>
      )}
      {!invoiceDetails?.invoice && (
        <Box
          ref={invoiceRef}
          sx={{
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 5,
            width: "100%",
            height: "80vh",
            maxWidth: "900px",
            marginTop: 15,
            marginBottom: 5,
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <div
            id="back-invoice-btn"
            onClick={() => navigate(-1)}
            style={{
              cursor: "pointer",
              color: "#2977CC",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ArrowBackIosNewIcon /> back
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80%",
              fontWeight: 600,
              fontSize: 20,
            }}
          >
            {" "}
            No invoice found!
          </div>
        </Box>
      )}
    </Box>
  );
};

export default ViewCustomInvoice;
